/** Section header, profit pill, and path list item type for FlowPathsPanel. */

export type FlowSectionCountKind = 'paths' | 'landers' | 'offers';

function countCaption(count: number, kind: FlowSectionCountKind): string {
  const noun =
    kind === 'paths'
      ? count === 1
        ? 'path'
        : 'paths'
      : kind === 'landers'
        ? count === 1
          ? 'lander'
          : 'landers'
        : count === 1
          ? 'offer'
          : 'offers';
  return `${count} ${noun}`;
}

export interface FlowPathListItem {
  id: string;
  name: string;
  weight: number;
  active: boolean;
  originalWeight?: number;
  originalActive?: boolean;
}

export function FlowPathsSectionTitle({
  title,
  count,
  countKind,
}: {
  title: string;
  count: number;
  countKind: FlowSectionCountKind;
}) {
  const caption = countCaption(count, countKind);
  return (
    <div className="flex items-center justify-between gap-2 pb-1 pt-0.5">
      <span className="text-xs font-medium text-muted-foreground">{title}</span>
      <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground" title={caption}>
        {caption}
      </span>
    </div>
  );
}

export function FlowPathsProfitTag({ profit }: { profit?: number }) {
  // Dev-only: the Winning / Negative / Break-even / No data status badge shows
  // while running locally (`yarn dev`) but is hidden in any deployed build
  // (vite build → dev/prod servers).
  if (!import.meta.env.DEV) return null;
  const base =
    'inline-flex items-center justify-center rounded-full border px-1.5 py-0 text-[10px] leading-tight';
  if (profit === undefined) {
    return <span className={`${base} bg-background text-muted-foreground`}>No data</span>;
  }
  if (profit > 0) {
    return (
      <span className={`${base} border-success/30 bg-success/10 font-medium text-success`}>
        Winning
      </span>
    );
  }
  if (profit < 0) {
    return (
      <span className={`${base} border-destructive/30 bg-destructive/10 font-medium text-destructive`}>
        Negative
      </span>
    );
  }
  return <span className={`${base} bg-background text-muted-foreground`}>Break-even</span>;
}
